import { useState, useCallback, memo, useEffect, useRef } from "react";
import { IconButton } from "@/components/atoms/IconButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { performanceMonitor } from '@/lib/performance';
import { prefetchRouteWithData } from '@/lib/route-preloader-enhanced';
import { prefetchData } from '@/lib/data-prefetcher';

export interface SidebarItem {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  route?: string;
}

export interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

export interface SidebarContainerProps {
  sections: SidebarSection[];
  isCollapsed: boolean;
  hoveredItemId: string | null;
  isNavigating: boolean;
  activeRoute?: string;
  onToggle: () => void;
  onMouseEnter: (itemId: string) => void;
  onMouseLeave: () => void;
  className?: string;
}

const SidebarItem = memo(({ 
  item, 
  isCollapsed, 
  itemId, 
  isHovered, 
  isNavigating,
  isActive,
  onMouseEnter, 
  onMouseLeave 
}: { 
  item: SidebarItem; 
  isCollapsed: boolean;
  itemId: string;
  isHovered: boolean;
  isNavigating: boolean;
  isActive: boolean;
  onMouseEnter: (itemId: string) => void;
  onMouseLeave: () => void;
}) => {
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  
  const handleMouseEnter = useCallback(() => {
    onMouseEnter(itemId);
    
    // Start route prefetching after 150ms hover (both collapsed and expanded modes)
    if (item.route) {
      prefetchTimerRef.current = setTimeout(() => {
        performanceMonitor.prefetchPage(item.route!);
        prefetchRouteWithData(item.route!);
        prefetchData(item.route!);
      }, 150);
    }
  }, [itemId, onMouseEnter, item.route]);
  
  const handleMouseLeave = useCallback(() => {
    onMouseLeave();
    
    // Cancel prefetch if user leaves before timer
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
    }
  }, [onMouseLeave]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (prefetchTimerRef.current) {
        clearTimeout(prefetchTimerRef.current);
      }
    };
  }, []);

  // Determine styling based on collapsed state and hover
  const shouldExpand = isCollapsed && isHovered;
  const width = isCollapsed ? (shouldExpand ? 'w-48' : 'w-12') : 'w-full';
  const zIndex = shouldExpand ? 'z-50' : 'z-10';
  

  return (
    <div className="relative overflow-visible">
      <IconButton
        buttonType="sidebar"
        onClick={item.onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`group sidebar-item-optimized relative overflow-hidden ${!isNavigating ? 'transition-[width,background-color,transform] duration-300 ease-in-out' : ''} ${width} ${zIndex}`}
        style={{ willChange: 'width, transform' }}
      >
        {/* Active indicator — owl beak shape extracted from logo */}
        <svg
          className={`absolute right-[3.2px] bottom-[3.2px] h-[10px] w-[7px] text-primary transition-opacity duration-200 ${isActive ? 'opacity-100' : 'opacity-0'}`}
          viewBox="194 69 27 31"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M221.23,85.12c-3.64,5.6-8.21,10.54-13.49,14.62-5.29-4.08-9.85-9.03-13.49-14.62,5.93-4.18,10.6-9.9,13.49-16.54,2.9,6.64,7.56,12.37,13.49,16.54Z" />
        </svg>
        <div className="absolute top-1/2 -translate-y-1/2 left-[11px] z-10">
          <item.icon className={`h-6 w-6 ${isActive ? 'text-primary' : 'text-foreground'}`} />
        </div>
        {(!isCollapsed || shouldExpand) && (
          <div className="absolute left-12 top-1/2 -translate-y-1/2">
            <span className={`text-sm whitespace-nowrap ${!isCollapsed ? 'group-hover:underline' : ''} ${isActive ? 'text-primary font-medium' : 'text-foreground'}`}>{item.label}</span>
          </div>
        )}
      </IconButton>
    </div>
  );
});

const SidebarSection = memo(({ 
  section, 
  isCollapsed, 
  hoveredItemId, 
  isNavigating,
  activeRoute,
  onMouseEnter, 
  onMouseLeave 
}: { 
  section: SidebarSection; 
  isCollapsed: boolean;
  hoveredItemId: string | null;
  isNavigating: boolean;
  activeRoute?: string;
  onMouseEnter: (itemId: string) => void;
  onMouseLeave: () => void;
}) => (
  <div className="flex flex-col gap-[1vh]">
    {section.title !== "Account" && (
      <div className="relative overflow-hidden min-h-3 h-[2vh] max-h-5">
        <h3 className={`absolute left-0 right-0 text-center text-xs font-medium text-foreground uppercase tracking-wider transition-opacity duration-200 ease-in-out opacity-100 transition-delay-100`}>
          {section.title}
        </h3>
      </div>
    )}
    <div className="flex flex-col gap-[0.8vh]">
      {section.items.map((item, index) => {
        const itemId = `${section.title}-${index}`;
        const isActive = !!(activeRoute && item.route && activeRoute.startsWith(item.route));
        return (
          <SidebarItem 
            key={itemId} 
            item={item} 
            isCollapsed={isCollapsed}
            itemId={itemId}
            isHovered={hoveredItemId === itemId}
            isNavigating={isNavigating}
            isActive={isActive}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        );
      })}
    </div>
  </div>
));

export function SidebarContainer({ 
  sections, 
  isCollapsed,
  hoveredItemId,
  isNavigating,
  activeRoute,
  onToggle,
  onMouseEnter,
  onMouseLeave,
  className 
}: SidebarContainerProps) {

  return (
    <div data-testid="sidebar" role="navigation" aria-label="Primary" className={cn(
      "flex flex-col relative will-change-[width] pl-6 pr-2 pt-0 h-[calc(100vh-6rem)] lg:fixed lg:left-0 lg:top-24 lg:z-30",
      isCollapsed ? 'overflow-visible' : 'overflow-hidden',
      !isNavigating ? 'transition-[width] duration-300 ease-in-out' : '',
      isCollapsed ? 'w-20' : 'w-52',
      className
    )}>
      {/* Toggle Button - Hidden for now */}
      {/* <IconButton 
        size="custom"
        customSize={{ width: "2.5rem", height: "2.5rem" }}
        onClick={onToggle}
        className="transition-[transform,background-color] duration-300 ease-in-out self-start mb-6"
      >
        {isCollapsed ? (
          <ChevronRight className="h-5 w-5 text-foreground" />
        ) : (
          <ChevronLeft className="h-5 w-5 text-foreground" />
        )}
      </IconButton> */}

      {/* Sidebar Content */}
      <div className="flex flex-col gap-[2.5vh]">
        {sections.map((section, index) => (
          <SidebarSection 
            key={`section-${index}`} 
            section={section} 
            isCollapsed={isCollapsed}
            hoveredItemId={hoveredItemId}
            isNavigating={isNavigating}
            activeRoute={activeRoute}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        ))}
      </div>
    </div>
  );
}